import React, { useRef, useState } from "react";
import {
  FaPaperclip,
  FaPaperPlane,
  FaFileCode,
  FaTimes,
  FaFileImage,
  FaFilePdf,
  FaSmile,
} from "react-icons/fa";
import EmojiPicker from "./EmojiPicker";

const MessageInput = ({
  onSendText,
  onSendMedia,
  onOpenTemplateModal,
  isSending,
}) => {
  const [text, setText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleSelectEmoji = (emojiChar) => {
    if (textareaRef.current) {
      const start = textareaRef.current.selectionStart || text.length;
      const end = textareaRef.current.selectionEnd || text.length;
      const newText = text.substring(0, start) + emojiChar + text.substring(end);
      setText(newText);

      // Restore cursor position after inserted emoji
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const newPos = start + emojiChar.length;
          textareaRef.current.setSelectionRange(newPos, newPos);
        }
      }, 0);
    } else {
      setText((prev) => prev + emojiChar);
    }
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
    // Auto-grow textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        150
      )}px`;
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (event) => setFilePreview(event.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const cancelFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSend = () => {
    setShowEmojiPicker(false);
    if (selectedFile) {
      onSendMedia(selectedFile, text);
      cancelFile();
      setText("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    } else if (text.trim()) {
      onSendText(text.trim());
      setText("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {/* File preview bar before sending */}
      {selectedFile && (
        <div className="wa-media-preview-bar">
          <div className="wa-media-preview-thumb">
            {filePreview ? (
              <img src={filePreview} alt="Preview" />
            ) : selectedFile.type.includes("pdf") ? (
              <FaFilePdf style={{ fontSize: 32, color: "#ea0038", margin: 14 }} />
            ) : (
              <FaFileImage style={{ fontSize: 32, color: "#00a884", margin: 14 }} />
            )}
          </div>
          <div className="wa-media-preview-info">
            <div className="name">{selectedFile.name}</div>
            <div className="type">{(selectedFile.size / 1024).toFixed(1)} KB</div>
          </div>
          <button className="wa-media-preview-cancel" onClick={cancelFile} title="Cancel file">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Emoji Picker Popover */}
      {showEmojiPicker && (
        <EmojiPicker
          onSelectEmoji={handleSelectEmoji}
          onClose={() => setShowEmojiPicker(false)}
        />
      )}

      {/* Input bar */}
      <div className="wa-input-area">
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileSelect}
          accept="image/*,application/pdf,.doc,.docx,.xlsx"
        />

        <div className="wa-input-actions">
          <button
            type="button"
            className={showEmojiPicker ? "active-emoji-btn" : ""}
            onClick={() => setShowEmojiPicker((prev) => !prev)}
            title="Choose Emoji"
          >
            <FaSmile style={{ color: showEmojiPicker ? "#00a884" : "inherit" }} />
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            title="Attach file (image/document)"
          >
            <FaPaperclip />
          </button>
          <button type="button" onClick={onOpenTemplateModal} title="Send Meta Approved Template">
            <FaFileCode />
          </button>
        </div>

        <div className="wa-input-wrapper">
          <textarea
            ref={textareaRef}
            rows={1}
            placeholder={
              selectedFile
                ? "Add a caption..."
                : "Type a message (Shift+Enter for new line)"
            }
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
          />
        </div>

        <button
          className="wa-send-btn"
          onClick={handleSend}
          disabled={isSending || (!text.trim() && !selectedFile)}
          title="Send"
        >
          <FaPaperPlane />
        </button>
      </div>
    </div>
  );
};

export default MessageInput;
